<template>
  <div class="scroll-container feed" ref="scrollContainer">
    <div class="feeds-wrapper" ref="feedsWrapper">
      <div class="container w-full" style="height: 20vh;"></div>
      <div class="columns">
        <div class="column" v-for="(column, index) in items" :key="index">
          <div v-for="(item, index) in column" :key="index">
            <FunctionalWorkGridItem @updated="gridUpdated" v-if="item.type === 'work'" :work="item.data" @item:updated="workUpdated(item.data._id)" />
            <ReadGridItem v-else-if="item.type === 'read'" :read="item.data" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useWorkStore } from '~/composables/useWorkStore';
import ReadGridItem from '~/components/reads/ReadGridItem.vue';
import TalentGridItem from '~/components/talents/TalentGridItem.vue';
import FunctionalWorkGridItem from '~/components/works/FunctionalWorkGridItem.vue';
import type {FeedUser, Read, Work} from "~/types/types";

const { loadBrandFeed, loadWork, loadPreviewFeed } = useWorkStore();
const works = ref<Work[] | any>([]);
const reads = ref<Read[] | any>([]);
const talents = ref<FeedUser[] | any>([]);
const scrollContainer = ref<any>(null);

const scrollStep = ref(1);
let scrollInterval = null;

onMounted(async () => {
  await loadData();
  startAutoScroll();
});

const loadData = async () => {
  const response = await loadPreviewFeed({ skip: 0, limit: 20 }) as any;
  works.value = [...response.works];
  reads.value = [...response.reads];
  talents.value = [...response.talents];
};

// This function controls the auto-scroll behavior
const startAutoScroll = () => {
  const container = scrollContainer.value as HTMLElement;

  scrollInterval = setInterval(() => {
    container.scrollTop += scrollStep.value;

    // Reverse scroll when reaching the bottom
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
      scrollStep.value = -1; // Reverse direction (scroll up)
    }

    // Reverse scroll when reaching the top
    if (container.scrollTop <= 0) {
      scrollStep.value = 1; // Reverse direction (scroll down)
    }
  }, 15); // Adjust the interval speed as necessary
};

const workUpdated = async (id: string) => {
  const newWork = await loadWork(id);
  const findIndex = works.value.findIndex((w: any) => w._id === id);
  works.value[findIndex] = newWork;
};

const gridUpdated = async () => {};

// Define items dynamically for the feed
const items = computed(() => {
  const columns = [[] as any, [] as any, [] as any, [] as any] as any;

  let combinedItems = [];
  let workCounter = 0;
  let readCounter = 0;
  let talentCounter = 0;

  while (workCounter < works.value.length || readCounter < reads.value.length || talentCounter < talents.value.length) {
    // Add a read if available
    if (readCounter < reads.value.length) {
      combinedItems.push({ type: 'read', data: reads.value[readCounter] });
      readCounter++;
    }

    // Add up to 3 works
    for (let i = 0; i < 3 && workCounter < works.value.length; i++) {
      combinedItems.push({ type: 'work', data: works.value[workCounter] });
      workCounter++;
    }

    // Add a talent if available
    if (talentCounter < talents.value.length) {
      combinedItems.push({ type: 'talent', data: talents.value[talentCounter] });
      talentCounter++;
    }
  }

  // Distribute combined items into columns
  combinedItems.forEach((item: any, index) => {
    columns[index % 4].push(item);
  });

  return columns;
});
</script>

<style scoped>
.scroll-container {
  height: 100vh;
  overflow-y: auto; /* Enable vertical scroll */
  position: relative;
}

.feeds-wrapper {
  display: flex;
  flex-direction: column;
}

.columns {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}
.mobile .columns {
  display: block;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.feed {
  padding: 88px 80px;
  width: 100%;
}

@media (max-width: 768px) {
  .columns {
    flex-direction: column;
    gap: 16px;
  }

  .column {
    width: 100%;
    flex: none;
  }

  .feed {
    padding: 16px;
  }
}
</style>
